import {FC, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useField} from 'formik';
import {List} from '@tickchak/react-share/app';
import {TShow} from '../../../../../hooks/useShow';
import {RootState} from '../../../../../redux/store.type';
import {TTimetableItem} from '../../../../../redux/timetable/timetable.type';
import {useTimetableDays} from '../../../hooks/useTimetableDays';
import BackArrow from '../../../../../assets/icons/BackArrow';
import * as css from './DaySelectPopup.css';

interface DaySelectPopupProps {
    show: TShow;
}

const DaySelectPopup: FC<DaySelectPopupProps> = ({show}) => {
    const {t} = useTranslation('timetable');
    const {conferenceDays} = useTimetableDays();
    const lecturesByDay = useSelector((state: RootState) => state.timetable.lectures);
    const [field, , helpers] = useField<number>('dayIndex');
    const [draftIndex, setDraftIndex] = useState<number>(field.value);

    useEffect(() => {
        if (show.isOpen) setDraftIndex(field.value);
    }, [show.isOpen, field.value]);

    if (!show.isOpen) return null;

    const getCount = (timestamp: number) =>
        (lecturesByDay[timestamp] ?? []).filter((l: TTimetableItem) => !l.isBreak).length;

    const handleConfirm = () => {
        helpers.setValue(draftIndex);
        show.close();
    };

    return (
        <List
            show={show}
            data-cy="daySelectPop"
            position={{width: '375px', height: '432px'}}
            ariaLabel={t('selectDayTitle')}
        >
            <List.Header
                fixed
                title={t('selectDayTitle')}
                onBack={() => show.close()}
                backIcon={<BackArrow />}
            />
            <List.Items>
                {conferenceDays.map((day, i) => {
                    const count = getCount(day.timestamp);
                    const selected = i === draftIndex;
                    return (
                        <List.Item.Custom key={day.key} id={String(i)}>
                            <css.item
                                type="button"
                                $selected={selected}
                                onClick={() => setDraftIndex(i)}
                                data-cy={`daySelect-${i}`}
                            >
                                <css.label $selected={selected}>{day.label}</css.label>
                                <css.badge $variant={count > 0 ? 'success' : 'error'}>
                                    {t('lecturesCount', {count})}
                                </css.badge>
                            </css.item>
                        </List.Item.Custom>
                    );
                })}
                <List.Item.Custom id="no-date">
                    <css.item
                        type="button"
                        $selected={draftIndex === -1}
                        onClick={() => setDraftIndex(-1)}
                        data-cy="daySelect-no-date"
                    >
                        <css.label $selected={draftIndex === -1}>{t('noDateLectures')}</css.label>
                    </css.item>
                </List.Item.Custom>
            </List.Items>
            <List.Footer fixed>
                <List.Footer.Close text={t('close')} size="category" />
                <List.Footer.Button
                    text={t('confirm')}
                    variant="brand"
                    size="category"
                    onClick={handleConfirm}
                />
            </List.Footer>
        </List>
    );
};

export default DaySelectPopup;
